import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import GlobalDateFilter from '../components/GlobalDateFilter';

import { MdPeople, MdCampaign, MdArticle, MdVisibility } from 'react-icons/md';

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [articleStats, setArticleStats] = useState([]);
  const [regionStats, setRegionStats] = useState([]);
  const [dauData, setDauData] = useState([]);
  const [adStats, setAdStats] = useState([]);
  const [appVersions, setAppVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  useEffect(() => {
    setRefreshing(true);
    const query = `?filter=${dateFilter}&from_date=${customRange.from}&to_date=${customRange.to}`;

    Promise.all([
      api.get(`/admin/analytics/overview${query}`),
      api.get(`/admin/analytics/articles${query}&limit=10`),
      api.get(`/admin/analytics/regions${query}`),
      api.get(`/admin/analytics/daily${query}`),
      api.get('/admin/analytics/ads'),
      api.get('/admin/analytics/app-versions'),
    ]).then(([ov, art, reg, dau, ads, versions]) => {
      setOverview(ov.data.data);
      setArticleStats(art.data.data);
      setRegionStats(reg.data.data);
      setDauData(dau.data.data);
      setAdStats(ads.data.data);
      setAppVersions(versions.data.data || []);
    }).catch(() => toast.error('Failed to load analytics'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [dateFilter, customRange.from, customRange.to]);

  if (loading) return <div className="page-loader">Loading...</div>;

  const sortedVersions = [...appVersions].sort((a, b) => (b.version_code || 0) - (a.version_code || 0));
  const latestVersion = sortedVersions.length > 0 ? sortedVersions[0].version_name : 'N/A';
  const totalVersions = sortedVersions.length;
  const totalUserDevices = sortedVersions.reduce((acc, curr) => acc + (curr.registered_users || 0), 0);
  const totalGuestDevices = sortedVersions.reduce((acc, curr) => acc + (curr.guest_users || 0), 0);

  return (
    <div className="page" style={{ opacity: refreshing ? 0.6 : 1, pointerEvents: refreshing ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Analytics</h2>
        <GlobalDateFilter 
          filter={dateFilter} 
          fromDate={customRange.from} 
          toDate={customRange.to}
          onChange={(f, from, to) => {
            setDateFilter(f);
            setCustomRange({ from, to });
          }}
        />
      </div>

      {/* Overview */}
      <div className="stats-grid">
        {[
          { label: 'Total Users', value: overview?.totalUsers || 0, icon: MdPeople, color: '#2563EB' },
          { label: 'Guest Devices', value: overview?.totalGuests || 0, icon: MdCampaign, color: '#10B981' },
          { label: 'Articles Today', value: overview?.articlesToday || 0, icon: MdArticle, color: '#8B5CF6' },
          { label: 'Reads Today', value: overview?.readsToday || 0, icon: MdVisibility, color: '#EF233C' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ color: s.color, fontSize: '24px' }}>
              <s.icon />
            </div>
            <div className="stat-info">
              <h3 style={{ color: s.color }}>{s.value}</h3>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* DAU Chart */}
      <div className="card chart-card">
        <h3>Daily Active Users (14 days)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={dauData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0d0d0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line type="monotone" dataKey="active_users" stroke="#DC2626" strokeWidth={2} dot={{ fill: '#DC2626' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Region Chart */}
      <div className="card chart-card">
        <h3>Reads by Region</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={regionStats}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0d0d0" />
            <XAxis dataKey="region" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="read_count" fill="#DC2626" radius={[6, 6, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Articles */}
      <div className="card">
        <h3 className="card-title">Top Articles (by views)</h3>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Views</th>
                <th>Likes</th>
                <th className="hide-mobile">Comments</th>
                <th className="hide-mobile">Category</th>
              </tr>
            </thead>
            <tbody>
              {articleStats.map(a => (
                <tr key={a.id}>
                  <td className="title-cell">{a.title}</td>
                  <td>{a.view_count}</td>
                  <td>{a.like_count}</td>
                  <td className="hide-mobile">{a.comment_count}</td>
                  <td className="hide-mobile"><span className="badge badge-category">{a.category?.replace('_', ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ad Performance */}
      <div className="card">
        <h3 className="card-title">Ad Performance</h3>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ad</th>
                <th>Type</th>
                <th>Impressions</th>
                <th>Taps</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {adStats.map(ad => (
                <tr key={ad.id}>
                  <td>{ad.title || `Ad #${ad.id}`}</td>
                  <td><span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span></td>
                  <td>{ad.impression_count}</td>
                  <td>{ad.tap_count}</td>
                  <td>{ad.ctr}%</td>
                </tr>
              ))}
              {adStats.length === 0 && <tr><td colSpan="5" className="empty-row">No ads</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* App Version Analytics */}
      <div className="card">
        <h3 className="card-title">⭐ App Version Analytics</h3>
        
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          {[
            { label: 'Latest Version', value: latestVersion },
            { label: 'Total Versions', value: totalVersions },
            { label: 'Total User Devices', value: totalUserDevices.toLocaleString() },
            { label: 'Total Guest Devices', value: totalGuestDevices.toLocaleString() },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-info">
                <h3>{s.value}</h3>
                <p>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Version Name</th>
                <th style={{ textAlign: 'right' }}>Version Code</th>
                <th style={{ textAlign: 'right' }}>Users</th>
                <th style={{ textAlign: 'right' }}>Guests</th>
                <th style={{ textAlign: 'right' }}>Total Devices</th>
              </tr>
            </thead>
            <tbody>
              {sortedVersions.map((v, i) => (
                <tr key={`${v.platform}_${v.version_code}_${i}`}>
                  <td>
                    <span className="badge badge-category" style={{ textTransform: 'capitalize' }}>
                      {v.platform}
                    </span>
                  </td>
                  <td>{v.version_name}</td>
                  <td style={{ textAlign: 'right' }}>{v.version_code}</td>
                  <td style={{ textAlign: 'right' }}>{(v.registered_users || 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{(v.guest_users || 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{(v.total || 0).toLocaleString()}</td>
                </tr>
              ))}
              {sortedVersions.length === 0 && (
                <tr>
                  <td colSpan="6" className="empty-row" style={{ textAlign: 'center' }}>
                    No App Version Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
